import type { AxiosResponse } from 'axios';
import React, { createContext, useCallback, useContext } from 'react';
import {
	extractOfDataQuery,
	useAxiosInstance,
	usePoMutation,
} from '@gov-nx/api';
import { useExtraConfigContext } from '@gov-nx/core/app';
import { useMessageEvents } from '@gov-nx/core/events';
import { useProcessControl, useServiceAuthorization } from '@gov-nx/core/hooks';
import { getRequiredDataBoxes, useLocale } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import { compose } from '@gov-nx/core/types';
import type { ServiceCode } from '../definitions/codes';
import { FormInstance, prepareSubmitData } from './FormDefenitions';
import type { FormData, ServiceContextTypes } from './service.types';
import { useAutorizaceContext } from 'libs/component/web/src/Autorizace/Context';

const AUTHORIZATION_SERVICE_NAME = 'INSOLVENCNI_REJSTRIK';

export const ServiceContext =
	createContext<Nullable<ServiceContextTypes>>(null);

export interface ServiceContextProviderProps {
	children: React.ReactNode;
	code: ServiceCode;
}

export function VypisZIRContextProvider({
	children,
	code,
}: ServiceContextProviderProps) {
	const { t } = useLocale(code);
	const { toastMessageSuccess } = useMessageEvents();
	const {
		extraConfig: { application },
	} = useExtraConfigContext();
	const requiredDataBoxes = getRequiredDataBoxes(code, application);
	const { controls, setControls } = useProcessControl();
	const { axiosInstance } = useAxiosInstance();
	const authContext = useAutorizaceContext();
	const { isLowAuthorization, isAuthorizationEnforced } =
		useServiceAuthorization(AUTHORIZATION_SERVICE_NAME);
	const formDefinition = FormInstance({
		dataBoxTypes: requiredDataBoxes,
		isLowAuthorization,
	});

	const submitMutation = usePoMutation<AxiosResponse<void>, FormData>({
		mutationFn: compose(extractOfDataQuery(axiosInstance), prepareSubmitData),
		onError: (error: Error) => {
			setControls({ processError: error, processLoading: false });
		},
		onSuccess: async (response) => {
			setControls({ processLoading: false });
			formDefinition.formReset();

			const nonHighAuthorizationFlow = () => {
				toastMessageSuccess(t('form.messages.odeslano'));
			};

			if (authContext) {
				authContext.onInitAuthorization(
					AUTHORIZATION_SERVICE_NAME,
					response.headers['x-autorizaceid'],
					nonHighAuthorizationFlow
				);
			} else {
				nonHighAuthorizationFlow();
			}
		},
	});

	const handleSubmit = useCallback(async () => {
		setControls({ processError: null, processLoading: true });
		submitMutation.mutate(formDefinition.formMethods.getValues());
	}, [formDefinition.formMethods, setControls, submitMutation]);

	const onSubmit = formDefinition.formMethods.handleSubmit(handleSubmit);

	return (
		<ServiceContext.Provider
			value={{
				code,
				formDefinition,
				submitMutation,
				onSubmit,
				controls,
				setControls,
				requiredDataBoxes,
				isLowAuthorization,
				isAuthorizationEnforced,
			}}>
			{children}
		</ServiceContext.Provider>
	);
}

export const useVypisInsolvencnihoRejstrikuContext = (): ServiceContextTypes =>
	useContext(ServiceContext) as ServiceContextTypes;
